import { ChangeEvent, useCallback, useContext, useEffect, useRef, useState } from "react";

import {
  formatCentsToEditableValue,
  formatCurrencyFromCents,
  FormType,
  getCurrencyFromCountry,
  getDollarValue,
  parseFormBackgroundTheme,
  sanitizeAmountInput,
  unformatDisplayValue,
} from "@simplyk/common";
import { useController, useWatch } from "react-hook-form";

import { AmplitudeEvents } from "@/common/constants/amplitude";
import { useLocaleContext } from "@/common/contexts/LocaleContext";
import { formBackgroundThemeToBurstTypeOrHeart } from "@/canvas/components/Burst/formBackgroundThemeToBurstType";
import { useBurst } from "@/canvas/features/FormV2/contexts/burstContext";
import { useFormV2Context } from "@/canvas/features/FormV2/contexts/FormV2Context";
import { HeartsAnimationContext } from "@/canvas/features/FormV2/contexts/heartsAnimationContext";
import { useAmplitude } from "@/common/hooks/amplitude/useAmplitude";
import { useTranslate } from "@/common/hooks/useTranslate";

const EXTRA_DONATION_MIN_AMOUNT = 100;
const EXTRA_DONATION_MAX_AMOUNT = 20_000_00;

export const useAdditionalDonationAmountField = () => {
  const { formObject: ticketing, form } = useFormV2Context(FormType.Ticketing);
  const { isoLocale, locale } = useLocaleContext();
  const { t } = useTranslate();
  const { logAmplitudeEvent } = useAmplitude();
  const extraDonation = useWatch({ control: form.control, name: "extraDonation" }) || 0;
  const { triggerHeartsAnimation } = useContext(HeartsAnimationContext);
  const { burstFromElement } = useBurst();
  const extraDonationBurstType = formBackgroundThemeToBurstTypeOrHeart(
    parseFormBackgroundTheme(ticketing.backgroundTheme)
  );

  const [previousExtraDonation, setPreviousExtraDonation] = useState(getDollarValue(extraDonation));

  const previousLocaleRef = useRef(locale);
  useEffect(() => {
    if (previousLocaleRef.current === locale) {
      return;
    }
    previousLocaleRef.current = locale;
    if (form.getValues("manualExtraDonation")) {
      form.setValue("manualExtraDonation", formatCentsToEditableValue(form.getValues("extraDonation") ?? 0, locale));
    }
  }, [locale, form]);

  const {
    field: {
      ref: manualExtraDonationFieldRef,
      onChange: onManualExtraDonationFieldChange,
      onBlur: onManualExtraDonationFieldBlur,
      ...manualExtraDonationField
    },
  } = useController({
    control: form.control,
    name: "manualExtraDonation",
    rules: {
      validate: {
        isMin: (value) => {
          if (value === undefined || value === null || value === "") {
            return true;
          }

          const parsedValue = parseFloat(unformatDisplayValue(value, locale));

          if (!isNaN(parsedValue) && (parsedValue * 100 >= EXTRA_DONATION_MIN_AMOUNT || parsedValue === 0)) {
            return true;
          }

          return t("ticketing", "extraDonMin", {
            amount: formatCurrencyFromCents(EXTRA_DONATION_MIN_AMOUNT, {
              isoLocale,
              currency: getCurrencyFromCountry(ticketing.organization?.country),
            }),
          });
        },
        isMax: (value) => {
          if (value === undefined || value === null || value === "") {
            return true;
          }

          const parsedValue = parseFloat(unformatDisplayValue(value, locale));

          if (!isNaN(parsedValue) && parsedValue * 100 <= EXTRA_DONATION_MAX_AMOUNT) {
            return true;
          }

          return t("ticketing", "extraDonMax", {
            amount: formatCurrencyFromCents(EXTRA_DONATION_MAX_AMOUNT, {
              isoLocale,
              currency: getCurrencyFromCountry(ticketing.organization?.country),
            }),
          });
        },
      },
    },
  });

  const {
    field: { value: extraDonationAmount, onChange: onExtraDonationAmountChange, ...extraDonationAmountField },
  } = useController({
    control: form.control,
    name: "extraDonation",
  });

  const logExtraDonationChangedEvent = useCallback(() => {
    const newAmount = getDollarValue(extraDonation);
    logAmplitudeEvent(AmplitudeEvents.DonorFormExtraDonationChanged, {
      formCategory: ticketing.formCategory,
      formId: ticketing.id,
      formType: FormType.Ticketing,
      organizationCountry: ticketing.organization?.country,
      donationAmountBefore: previousExtraDonation,
      donationAmountAfter: newAmount,
    });
    setPreviousExtraDonation(newAmount);
  }, [
    extraDonation,
    logAmplitudeEvent,
    previousExtraDonation,
    ticketing.formCategory,
    ticketing.id,
    ticketing.organization?.country,
  ]);

  const handleOnManualExtraDonationChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const value = sanitizeAmountInput(e.target.value);

      onManualExtraDonationFieldChange(value);

      const parsedValue = parseFloat(unformatDisplayValue(value, locale));

      if (!isNaN(parsedValue) && parsedValue < 0) {
        return;
      }

      const numberValue = isNaN(parsedValue) || parsedValue < 0 ? 0 : parsedValue * 100;

      onExtraDonationAmountChange(numberValue);
      triggerHeartsAnimation();
      if (numberValue > extraDonation) {
        burstFromElement(e.target, { type: extraDonationBurstType, align: "left" });
      }
    },
    [
      onExtraDonationAmountChange,
      onManualExtraDonationFieldChange,
      triggerHeartsAnimation,
      burstFromElement,
      extraDonation,
      extraDonationBurstType,
      locale,
    ]
  );

  return {
    extraDonation,
    extraDonationAmount,
    extraDonationAmountField,
    manualExtraDonationField,
    manualExtraDonationFieldRef,
    onManualExtraDonationFieldBlur,
    handleOnManualExtraDonationChange,
    logExtraDonationChangedEvent,
    errorMessage: form.formState.errors.manualExtraDonation?.message,
  };
};
